/**
 * Which CAD instance the app is working with — **one selection per vendor**,
 * app-wide (#5).
 *
 * Everything that reaches a CAD host reads this: chat (one id per vendor on
 * every turn), skill execution, and skill editing. One selection per vendor
 * rather than one per feature, because "which Revit am I driving" is a fact
 * about the session, not about the screen you happen to be on — and two
 * features disagreeing about it is a write to the wrong document.
 *
 * Per vendor rather than app-wide (owner, 2026-09-04): a Revit and an AutoCAD
 * are both connected at once, and the agent routes each ask to the vendor the
 * prompt is about. Within a vendor nothing changes — one instance in play, the
 * user picks it, the agent never chooses between two Revits. The old app-wide
 * default — oldest instance regardless of vendor — quietly sent an AutoCAD
 * question to Revit because Revit was opened first; keyed by vendor, that
 * cannot happen.
 *
 * Pure and separate from the components for the same reason
 * `hostStatus.ts` is: these rules decide **which model gets written to**, and a
 * rule that only exists inside a dropdown's `onChange` is a rule no test can
 * hold to.
 */
import { HOST_KIND_ORDER, groupByVendor, sortInstances, type HostKind } from "./hostStatus";
import type { CadInstance } from "../bimgo";

export type { HostKind };

export type SelectionStatus =
  /** The user picked this one and it is still running. */
  | "selected"
  /** Nothing was picked, so the first one is used. */
  | "default"
  /** Nothing is running. */
  | "none";

export interface Selection {
  status: SelectionStatus;
  /** The instance every CAD-bound feature should address, or null. */
  instance: CadInstance | null;
}

/**
 * Resolve the selection against what is actually running.
 *
 * **The first instance is the default**, in `sortInstances` order — oldest
 * first, which is the one the user opened first and the one they are most
 * likely to mean. Defaulting is safe in a way that *substituting* is not: it
 * happens when no choice has been expressed, and the instance card names the result
 * either way.
 *
 * **A choice that has closed falls back to the default** (#189). It used to be
 * held as "selected instance closed" and its dead id sent with every turn, so the
 * agent dropped that vendor's tools until the user picked again: close AutoCAD
 * 2024, open 2025, and `send_code_to_autocad` was gone — every instance is a new
 * session, so a pick can never come back. The instance card names the instance in use
 * either way. The agent still refuses an id that closes between the send and the
 * turn (`routes/ask.ts`, `socket/instances.ts`); that race is the only "closed"
 * left.
 */
export function resolveSelectedInstance(
  selectedSessionId: string | null,
  instances: readonly CadInstance[],
): Selection {
  const sorted = sortInstances(instances);

  if (sorted.length === 0) {
    return { status: "none", instance: null };
  }

  if (selectedSessionId) {
    const found = sorted.find((i) => i.sessionId === selectedSessionId);
    if (found) return { status: "selected", instance: found };
  }

  return { status: "default", instance: sorted[0]! };
}

/**
 * May the selection change right now?
 *
 * **Not while a turn is streaming.** The tools for a turn are chosen when it
 * starts — the agent asks the host what it advertises and builds the tool set
 * from the answer — so a switch mid-stream cannot move the turn that is running.
 * Leaving the control live would only mislead: it would name one document while
 * the model wrote to another.
 */
export function canSwitchInstance(busy: boolean, instances: readonly CadInstance[]): boolean {
  return !busy && instances.length > 0;
}

/**
 * The product years one vendor's picker may switch to — set while an AutoCAD skill is open
 * in the Skills pane, to the years it supports (owner, 2026-10-24). Switching to an AutoCAD
 * of another year would leave the skill open where it can neither run nor be edited, so the
 * picker offers only the supported ones; a cross-version build is how a year is added.
 */
export interface InstanceRestriction {
  kind: HostKind;
  versions: readonly string[];
}

/** May this instance be picked under the restriction? Other vendors are never restricted. */
export function instanceAllowed(
  restriction: InstanceRestriction | null,
  kind: HostKind,
  instance: Pick<CadInstance, "hostVersion" | "revitVersion"> | undefined,
): boolean {
  if (!restriction || restriction.kind !== kind) return true;
  const year = (instance?.hostVersion ?? instance?.revitVersion ?? "").trim();
  return restriction.versions.includes(year);
}

/**
 * The picker's one line about the selection.
 *
 * **The name only — no count.** The picker's job is to answer "which Revit am I
 * driving", and that is one document title. How many are connected is a
 * different question, asked far less often, and it is answered by opening the
 * control: the list is the count. Carrying both made the line grow a prefix
 * (`2 · Tower.rvt`) that reads as part of the name at a glance.
 */
export function describeSelection(
  selection: Selection,
  label: (instance: CadInstance) => string,
  words: { none: string },
): string {
  if (selection.status === "none") return words.none;
  return label(selection.instance!);
}

// --- per vendor (#5) ------------------------------------------------------------

/** What the user picked, per vendor. Absent = no pick, so the vendor's default applies. */
export type VendorPicks = Partial<Record<HostKind, string>>;

/** The resolved selection of every vendor that has something running or picked. */
export interface Selections {
  /** In `HOST_KIND_ORDER`, only vendors with an entry — one picker row each. */
  vendors: HostKind[];
  byVendor: Partial<Record<HostKind, Selection>>;
}

export const NO_SELECTION: Selection = { status: "none", instance: null };

/**
 * Resolve every vendor's pick against what is actually running.
 *
 * Each vendor gets `resolveSelectedInstance` over **its own** instances, so the
 * rules stand unchanged within a vendor: oldest is the default, and a closed pick
 * falls back to it. A vendor with no instances and no pick has no entry; one with
 * a pick that has closed and nothing else running is `none`.
 */
export function resolveSelections(picks: VendorPicks, instances: readonly CadInstance[]): Selections {
  const grouped = groupByVendor(instances);
  const vendors: HostKind[] = [];
  const byVendor: Partial<Record<HostKind, Selection>> = {};
  for (const kind of HOST_KIND_ORDER) {
    const own = grouped[kind] ?? [];
    if (own.length === 0 && !picks[kind]) continue;
    vendors.push(kind);
    byVendor[kind] = resolveSelectedInstance(picks[kind] ?? null, own);
  }
  return { vendors, byVendor };
}

/** A vendor's selection, or `none` — the shape a skill or a pane can hold. */
export function selectionFor(selections: Selections, kind: HostKind): Selection {
  return selections.byVendor[kind] ?? NO_SELECTION;
}

/**
 * What a turn sends: one id per vendor — the instance in use. `none` sends
 * nothing: there is no instance.
 */
export function targetSessions(selections: Selections): Partial<Record<HostKind, string>> {
  const out: Partial<Record<HostKind, string>> = {};
  for (const kind of selections.vendors) {
    const selection = selections.byVendor[kind];
    if (!selection) continue;
    if (selection.instance) out[kind] = selection.instance.sessionId;
  }
  return out;
}

/**
 * The one instance features that still take a single id address: the Revit
 * selection when there is one, else the first vendor's. The document context
 * and the `@` menu are Revit's; the starter prompts take whichever is bound.
 */
export function primarySelection(selections: Selections): Selection {
  return selections.byVendor["revit"]?.instance
    ? selections.byVendor["revit"]!
    : selections.byVendor[selections.vendors[0] as HostKind] ?? selections.byVendor["revit"] ?? NO_SELECTION;
}
